const DEFAULT_MAX_ATTEMPTS = 3;
const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 10000;
const JITTER_MS = 500;

const RETRYABLE_NETWORK_CODES = new Set([
  "ECONNABORTED",
  "ECONNRESET",
  "ETIMEDOUT",
  "EPIPE",
  "ERR_NETWORK",
]);

const RETRYABLE_READ_STATUS = new Set([502, 503, 504]);

const MUTATING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function getBackoffDelayMs(attemptIndex) {
  const exponential = Math.min(BASE_DELAY_MS * 2 ** attemptIndex, MAX_DELAY_MS);
  const jitter = Math.floor(Math.random() * JITTER_MS);
  return exponential + jitter;
}

/**
 * Transport-agnostic retry decision from normalized failure fields.
 */
function isRetryableFailure({ status, networkCode, method = "GET" } = {}) {
  const methodUpper = method.toUpperCase();

  if (networkCode && RETRYABLE_NETWORK_CODES.has(networkCode) && !MUTATING_METHODS.has(methodUpper)) {
    return true;
  }

  if (MUTATING_METHODS.has(methodUpper)) {
    return false;
  }

  return status != null && RETRYABLE_READ_STATUS.has(status);
}

/**
 * Generic retry loop. Callers supply isRetryableError(error, method).
 */
async function retryWithBackoff(fn, options = {}) {
  const maxAttempts = options.maxAttempts !== undefined ? options.maxAttempts : DEFAULT_MAX_ATTEMPTS;
  const method = options.method !== undefined ? options.method : "GET";
  const isRetryableError = options.isRetryableError;
  const onRetry = options.onRetry;

  if (typeof isRetryableError !== "function") {
    throw new Error("retryWithBackoff requires isRetryableError(error, method)");
  }

  if (maxAttempts < 1) {
    throw new Error("retryWithBackoff requires maxAttempts >= 1");
  }

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      const canRetry =
        attempt < maxAttempts - 1 && isRetryableError(error, method);

      if (!canRetry) {
        throw error;
      }

      const delayMs = getBackoffDelayMs(attempt);
      if (typeof onRetry === "function") {
        onRetry({ attempt: attempt + 1, delayMs, error });
      }
      await sleep(delayMs);
    }
  }
}

module.exports = {
  DEFAULT_MAX_ATTEMPTS,
  RETRYABLE_NETWORK_CODES,
  getBackoffDelayMs,
  isRetryableFailure,
  retryWithBackoff,
};
